import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {v4 as uuid} from 'uuid';
import {SortableContainer, SortableElement, arrayMove} from 'react-sortable-hoc';
import {NotificationManager} from 'react-notifications';
import {Prompt} from 'react-router';

import * as api from '../../lib/api';
import * as flags from '../../lib/flags';
import {getErrorMessage} from '../../lib/helpers';
import * as usageLimits from '../../lib/usageLimits';
import {withUser} from '../../lib/user';
import {convertLibraryTemplate} from '../../lib/template-library';
import Screen from '../../components/Screen';
import Form from '../../components/Form';
import {default as UnstyledContent} from '../../components/Content';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Placeholder from '../../components/Placeholder';
import Notification from '../../components/Notification';
import ChipInput from '../../components/ChipInput';
import Icon from '../../components/Icon';

import {
  getValidationErrors,
  flattenValidationErrors,
  createQuestion,
  withoutSharedAnswerSettings,
  withSharedAnswerSettings,
  receivesSharedAnswers,
  toSavedQuestion,
  hasUnsavableSharedAnswers,
  withInventoryLotRemapped,
  withInventoryLotNormalised,
  isInventoryQuestion
} from './shared';
import Checklist from './Checklist';
import QuestionBuilder from './QuestionBuilder';
import ElementBuilder from './ElementBuilder';
import AlertBuilder from './AlertBuilder';
import NotificationBuilder from './NotificationBuilder';
import ContextAlertBuilder from './ContextAlertBuilder';
import ImportQuestionModal from './ImportQuestionModal';
import Menu from '../../components/Menu';
import AlertDialog from "../../components/AlertDialog";
import Card from "../../components/Card";

const MAX_TESTERS = 3;

class FormBuilderScreen extends PureComponent {
  state = {
    changed: false,
    enableUserGroups: false,
    enableFormTesters: false,
    enableFormReviewers: false,
    enableBulkReview: true,
    enableSecondLevelReview: false,
    form: {},
    catalog: [],
    catalogTypes: [],
    userGroups: [],
    users: [],
    notifications: [],
    loading: true,
    error: false,
    saving: false,
    importQuestion: false,
    showAddQuestionOptions: false,
    showFormElementList: false,
    maxTesters: MAX_TESTERS,
    dialog: null,
    savedQuestionIds: new Set()
  };

  async componentDidMount() {
    const {user, credentials, match: {params: {id, libraryTemplateId}}} = this.props;

    try {
      //starting from a library template, so fetch its definition and clone it into a new unsaved form
      const example = libraryTemplateId
        ? convertLibraryTemplate(await api.getLibraryTemplate(libraryTemplateId, credentials), user)
        : undefined;

      let [form, catalog, catalogTypes, userGroups, users, forms] = await Promise.all([
        id ? api.getTemplate(id, credentials) : Promise.resolve({
          id: uuid(),
          name: '',
          groups: [],
          betaTestUsers: [],
          contextQuestions: [],
          questions: [createQuestion(0)],
          alerts: [],
          contextAlerts: [],
          multi: false,
          catalogTypes: [],
          rowCompleteContacts: [],
          sheetCompleteContacts: [],
          rowApprovedContacts: [],
          rowRejectedContacts: [],
          reviewers: [],
          secondLevelReviewEnabled: false,
          secondLevelReviewers: [],
          reviewAllEnabled: true,
          inventoryPostAfterApproval: false,
          elements: [],
          ...example
        }),
        api.getCatalog(credentials),
        api.getCatalogTypes(credentials),
        api.getUserGroups(credentials),
        api.getAllUsers(credentials),
        api.getCompanyTemplates(credentials)
      ]);

      // Add a toString method to the users & user groups for their display in the chip input.
      userGroups.forEach(group => group.toString = () => group.name);
      form.groups.forEach(group => group.toString = () => group.name);
      users.forEach(user => user.toString = () => user.userName);
      form.alerts.forEach(alert => {
        alert.alertContacts.forEach(contact => contact.toString = () => contact.userName);
      });

      // add temporary ids for positioning of user expressions in notification list
      const addTempIds = (userExprs) => userExprs.map(ue=> ({
        ...ue,
        tempId: ue.tempId || uuid()
      }))

      form.rowCompleteContacts = addTempIds(form.rowCompleteContacts)
      form.rowApprovedContacts = addTempIds(form.rowApprovedContacts)
      form.rowRejectedContacts = addTempIds(form.rowRejectedContacts)

      form.sheetCompleteContacts.forEach(user => user.toString = () => user.userName);
      form.rowCompleteContacts.forEach(userExpr => userExpr.toString = () => userExpr.user.userName);
      form.rowApprovedContacts.forEach(userExpr => userExpr.toString = () => userExpr.user.userName);
      form.rowRejectedContacts.forEach(userExpr => userExpr.toString = () => userExpr.user.userName);
      form.betaTestUsers.forEach(user => user.toString = () => user.userName);
      form.reviewers.forEach(user => user.toString = () => user.userName);
      (form.secondLevelReviewers || []).forEach(user => user.toString = () => user.userName);

      // Convert MULTI_NUMERIC questions with isNumericGrid flag to NUMERIC_GRID for editing
      const convertMultiNumericGrids = (questions) => {
        return questions.map(q => {
          const isNumericGridMeta = q.questionMeta && q.questionMeta.find(m => m.key === 'isNumericGrid');
          if (q.type === 'MULTI_NUMERIC' && isNumericGridMeta && isNumericGridMeta.value === 'true') {
            return {
              ...q,
              type: 'NUMERIC_GRID',
              questionMeta: q.questionMeta.filter(m => m.key !== 'isNumericGrid')
            };
          }
          return q;
        });
      };

      // Convert MULTI_NUMERIC grids to NUMERIC_GRID for editing. A usage question saved with a lot question before
      // the setting was taken off usage questions goes back to the default, so nothing hidden holds up a save.
      form.questions = convertMultiNumericGrids(form.questions).map(withInventoryLotNormalised);
      form.contextQuestions = convertMultiNumericGrids(form.contextQuestions);

      // Calculate the notifications. Notifications are stored seperately from the form because they have no defined
      // order, but are presented to the user as if they have an order.
      const notifications = [
        form.sheetCompleteContacts.length > 0 && {
          id: uuid(),
          type: 'sheetCompleteContacts',
          users: form.sheetCompleteContacts
        },
        form.rowCompleteContacts.length > 0 && {
          id: uuid(),
          type: 'rowCompleteContacts',
          users: form.rowCompleteContacts
        },
        form.rowApprovedContacts.length > 0 && {
          id: uuid(),
          type: 'rowApprovedContacts',
          users: form.rowApprovedContacts
        },
        form.rowRejectedContacts.length > 0 && {
          id: uuid(),
          type: 'rowRejectedContacts',
          users: form.rowRejectedContacts
        },
      ].filter(Boolean);
      const originalQuestionList = form.questions.concat(form.contextQuestions);
      // Only an existing form was loaded from the server. A new form, including one started from a library template,
      // is built here with ids the server has never seen.
      const savedQuestionIds = new Set(id ? originalQuestionList.map(q => q.id) : []);

      this.setState({
        form :{
          ...form,
          originalQuestionList: originalQuestionList
        },
        catalog,
        catalogTypes,
        userGroups,
        users,
        notifications,
        enableUserGroups: form.groups.length > 0,
        enableFormTesters: form.betaTestUsers.length > 0 && !form.published && form.current,
        enableFormReviewers: form.reviewers.length > 0,
        enableSecondLevelReview: form.secondLevelReviewEnabled || false,
        enableBulkReview: form.reviewAllEnabled !== false,
        maxTesters: usageLimits.get(user, 'BETA_TESTERS_MAXIMUM', MAX_TESTERS),
        forms,
        originalQuestionList,
        savedQuestionIds
      }, () => this.loadSharedAnswerSources());
    }
    catch(e) {
      this.setState({error: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  handleSubmit = async () => {
    const {credentials, match: {params: {id}}, history} = this.props;
    const {form} = this.state;
    const validations = getValidationErrors(form);

    if (flattenValidationErrors(validations).length > 0) {
      // Can't submit the form because there are validation issues.
      return this.setState({validationEnabled: true});
    }

    const unsavable = [...form.contextQuestions, ...form.questions].find(hasUnsavableSharedAnswers);

    if (unsavable) {
      return NotificationManager.error(`The linked sources for "${unsavable.text}" could not be loaded, so the form can't be saved without losing them. Reload the page and try again.`);
    }

    this.setState({saving: true});

    try {
      const method = id ? 'editTemplate' : 'createTemplate';
      const redirectUrl = form.published ? `/forms` : `/forms?type=unpublished`;

      // Convert NUMERIC_GRID questions to MULTI_NUMERIC before saving
      const convertGridsForSave = (questions) => {
        return questions.map(q => {
          if (q.type === 'NUMERIC_GRID') {
            return {
              ...q,
              type: 'MULTI_NUMERIC',
              questionMeta: [
                ...q.questionMeta,
                {key: 'isNumericGrid', value: 'true'}
              ]
            };
          }
          return q;
        });
      };

      const formToSave = {
        ...form,
        questions: convertGridsForSave(form.questions).map(toSavedQuestion),
        contextQuestions: convertGridsForSave(form.contextQuestions).map(toSavedQuestion)
      };
      await api[method](formToSave, credentials);

      NotificationManager.success(`Your "${form.name}" form was saved`);
      this.setState({changed: false}, () => history.push(redirectUrl));
    }
    catch (e) { 
      // The api explains why it turned the form down, for example when a change would break a shared answers link.
      const message = e && (e.status === 400 || e.status === 403)
        ? await getErrorMessage(e, `There was a problem while saving your form`)
        : `There was a problem while saving your form`;

      NotificationManager.error(message);
      this.setState({saving: false});
    }
  }

  setFormState = (reducer, callback) => {
    this.setState(prevState => ({
      changed: true,
      form: {
        ...prevState.form,
        ...(typeof reducer === 'function' ? reducer(prevState.form) : reducer)
      }
    }), callback);
  }

  handleFormNameChanged = e => {
    const {form} = this.state;

    if (form.name !== e.target.value) {
      this.setFormState({name: e.target.value});
    }
  }

  handleQuestionChanged = (question, onComplete) => {
    this.setFormState(prevForm => {
      const questions = [...prevForm.contextQuestions, ...prevForm.questions];
      const prevQuestion = questions.filter(q => q.id === question.id)[0];

      // Handle the case when a user decides that a question should be context or not.
      if (prevQuestion && prevQuestion.context !== question.context) {
        const prevKey = prevQuestion.context ? 'contextQuestions' : 'questions';
        const nextKey = prevQuestion.context ? 'questions' : 'contextQuestions';

        // We need to move the question from the contextQuestions list to the questions list, or vice-versa.
        return {
          [prevKey]: prevForm[prevKey].filter(q => q.id !== question.id),
          [nextKey]: [...prevForm[nextKey], question]
        };
      }

      const key = question.context ? 'contextQuestions' : 'questions';

      return {
        [key]: prevForm[key].map(q => q.id === question.id ? question : q)
      };
    }, onComplete);
  }

  // A receiving question lists the sources linked to it on the server. They are loaded once, when the form opens, and
  // then edited with the rest of the form. A list that fails to load is marked null so it can't be saved over.
  loadSharedAnswerSources = async () => {
    const {credentials, user} = this.props;
    const {form, savedQuestionIds} = this.state;

    if (!flags.isOn(user, 'INVENTORY_ENABLED')) {
      return;
    }

    const receivingQuestions = [...form.contextQuestions, ...form.questions].filter(receivesSharedAnswers);
    const sourcesById = {};

    await Promise.all(receivingQuestions.map(async question => {
      // A question the server has never saved, such as one from a library template, has no links yet.
      if (!savedQuestionIds.has(question.id)) {
        sourcesById[question.id] = [];
        return;
      }

      try {
        sourcesById[question.id] = await api.getReceivingQuestionLinks(question.id, credentials);
      }
      catch (e) {
        sourcesById[question.id] = null;
      }
    }));

    if (receivingQuestions.some(question => sourcesById[question.id] === null)) {
      NotificationManager.error('Some linked sources could not be loaded. Reload the page to try again.');
    }

    this.setState(prevState => {
      const withSources = questions => questions.map(q => q.id in sourcesById ? {...q, sharedAnswerSources: sourcesById[q.id]} : q);

      return {
        form: {
          ...prevState.form,
          contextQuestions: withSources(prevState.form.contextQuestions),
          questions: withSources(prevState.form.questions)
        }
      };
    });
  }

  // When a question on this form stops being a source, the receiving questions on this form that list it drop it too.
  // Receiving questions on other forms are unlinked by the api when the form is saved.
  handleSharedAnswerSourceRemoved = sourceQuestionId => {
    const unlink = questions => questions.map(q => (q.sharedAnswerSources || []).some(s => s.sourceQuestionId === sourceQuestionId)
      ? withSharedAnswerSettings(q, {sources: q.sharedAnswerSources.filter(s => s.sourceQuestionId !== sourceQuestionId)})
      : q);

    this.setFormState(form => ({
      contextQuestions: unlink(form.contextQuestions),
      questions: unlink(form.questions)
    }));
  }

  // The forms the shared answers source picker offers, including this one with its current questions, so a question
  // made a source a moment ago can be picked straight away.
  getSharedAnswerSourceForms = () => {
    const {form, forms} = this.state;

    return [...(forms || []).filter(f => f.id !== form.id), form];
  }

  handleElementChanged = (element) => {
    this.setFormState(form => ({
      elements: form.elements.map(e => {
        if (e.id === element.id) {
          return element;
        }

        return e;
      })
    }));
  }

  handleAddQuestion = question => {
    const uniqueSortIndex = -1000;

    let predeterminedOrder = question && (question.order || question.order === 0) ? true : false;

    this.setFormState(prevForm => {
      const key = question && question.context ? 'contextQuestions' : 'questions';
      const order = question && (question.order || question.order === 0) ? question.order: maxSortOrder(prevForm) + 1;
      const newQuestion = question ? copyQuestion(question, order, prevForm) : createQuestion(order);

      return {
        [key]: [...prevForm[key], newQuestion]
      };
    }, () => {
      if (question && !predeterminedOrder) {
        // Reposition the copied question under the original question if its order isnt predetermined.
        this.handleSortElements({oldIndex: uniqueSortIndex, newIndex: question.order + 1});
      } else {
       // Reshuffle the order after adding a new element
       this.handleSortElements({oldIndex: uniqueSortIndex, newIndex: 0});
      }
    });
  }

  handleRemoveQuestion = (question, newQuestion) => {
    this.setFormState(prevForm => {
      const key = question.context ? 'contextQuestions' : 'questions';
      const updatedQuestions = prevForm[key].map(q=> {
        const removeQuestion = q.id === question.id; // only remove the correct question
        if (this.state.form.published && !newQuestion && removeQuestion){ //if it's not a new question and the form is already published
          return {
            ...q,
            required: false, // remove the required state
            deleted: true,
            condition: null,
            questionMeta: [], // drop the question metadata (colours, widths, units, etc.)
            order: -1 // set order to -1 to not interfere with sort order
          }
        }
        return q;
      }).filter(q => !(q.id === question.id && (!prevForm.published || newQuestion)));
      // do not remove it from the question list to avoid database issues if it was an already published form

      return {
        [key]: updatedQuestions
      };
    })
  };

  handleRemoveElement = element => {
    this.setFormState(form => ({
      elements: form.elements.filter(e => e.id !== element.id)
    }));
  }

  handleSortElements = ({oldIndex, newIndex}) => {
    const {form} = this.state;
    const allQuestions = [...form.contextQuestions, ...form.questions];
    const deletedQuestions = allQuestions.filter(q => q.deleted);
    const prevQuestions = allQuestions.filter(q => !q.deleted);
    const prevElements = [
      ...prevQuestions.map(q => ({ type: 'QUESTION', order: q.order, question: q })),
      ...form.elements.map(e => ({ type: 'ELEMENT', order: e.order, element: e }))
    ].sort((a, b) => a.order - b.order);

    const [contextQuestions, questions, elements] = arrayMove(prevElements, oldIndex, newIndex)
      .map((element, i) => ({
        ...element,
        order: i,
        [element.type.toLowerCase()]: {
          ...element[element.type.toLowerCase()],
          order: i
        }
      }))
      .reduce((buckets, element) => {
        // Categorize each question as either a context question or regular question or an element.
        if (element.type === 'QUESTION') {
          buckets[element.question.context ? 0 : 1].push(element.question);
        }
        else if (element.type === 'ELEMENT') {
          buckets[2].push(element.element);
        } else {
          throw new Error('Unknown element type ' + element.type);
        }

        return buckets;
      }, [[], [], []]);

    deletedQuestions.forEach(q => {
      (q.context ? contextQuestions : questions).push(q);
    }); // add deleted questions back after the sort

    this.setFormState({
      contextQuestions,
      questions,
      elements
    });
  }

  closeAlertDialog = () => {
    this.setState({dialog: null});
  }

  openAlertDialog = props => {
    this.setState({dialog: props});
  }

  handleUserGroupsChanged = groups => {
    if (groups.length !== this.state.form.groups.length) { // Popup only shows up when user groups are added or removed
      const {form, forms} = this.state;

      // Find all templates that have a launch alert pointing to the current form
      const linkedTemplates = (forms || []).filter(template =>
        Array.isArray(template.contextAlerts) &&
        template.contextAlerts.some(ca => ca.formId === form.id)
      );

      if (linkedTemplates.length > 0) {
        const templateNames = linkedTemplates.map(t => `"${t.name}"`).join(', ');
        let description = "When User Groups are added or removed the launch alerts associated with this template may be negatively impacted." +
            "\nEnsure Users have the ability to access the forms that are being launched when adding and removing user groups. " +
            `\n\nThis form is used as a launch destination in the following template(s): ${templateNames}.`;
        this.openAlertDialog({
          title: "Attention",
          description,
          extraButtons: [
            <Button key="to_user_groups" onClick={() => { window.open("/users/groups", "_blank"); }}>Manage User Groups</Button>,
          ]
        });
      } // add templates that use this specific form as a launch alert
    }
    this.setFormState({groups});
  }

  handleTestersChanged = betaTestUsers => {
    if (betaTestUsers.length > this.state.maxTesters) {
      return;
    }

    this.setFormState({betaTestUsers});
  }

  handleReviewersChanged = reviewers => {
    this.setFormState({reviewers});
  }

  handleSecondLevelReviewersChanged = secondLevelReviewers => {
    this.setFormState({secondLevelReviewers});
  }

  handleToggleSecondLevelReview = e => {
    const enableSecondLevelReview = e.target.checked;

    this.setState(prevState => {
      const form = mergeFormNotifications({
        ...prevState.form,
        secondLevelReviewEnabled: enableSecondLevelReview,
        secondLevelReviewers: enableSecondLevelReview ? prevState.form.secondLevelReviewers : []
      }, prevState.notifications);

      return {changed: true, enableSecondLevelReview, form};
    });
  }

  handleToggleBulkReview = e => {
    const enableBulkReview = e.target.checked;

    this.setState({enableBulkReview});
    this.setFormState({reviewAllEnabled: enableBulkReview});
  }

  handleToggleInventoryPostAfterApproval = e => {
    this.setFormState({inventoryPostAfterApproval: e.target.checked});
  }

  handleAlertChanged = alert => {
    this.setFormState(prevForm => ({
      alerts: prevForm.alerts.map(a => a.id === alert.id ? alert : a)
    }));
  }

  handleRemoveAlert = alert => {
    this.setFormState(prevForm => ({
      alerts: prevForm.alerts.filter(a => a.id !== alert.id)
    }));
  }

  handleAddAlert = () => {
    this.setFormState(prevForm => ({
      alerts: [...prevForm.alerts, createAlert()]
    }));
  }

  handleAddContextAlert = () => {
    this.setFormState(prevForm => ({
      contextAlerts: [...prevForm.contextAlerts, createContextAlert()]
    }));
  }

  handleCopyAlert = alertToCopy => {
    // inserts itself below the parent alert, order is not persisted
    this.setFormState(prevForm => {
      const index = prevForm.alerts.findIndex(alert => alert.id === alertToCopy.id);
      let newAlerts = [...prevForm.alerts];
      newAlerts.splice(index + 1, 0, copyAlert(alertToCopy));
      return { alerts: newAlerts };
    });
  }

  handleCopyContextAlert = contextAlertToCopy => {
    // inserts itself below the parent launch alert, order is not persisted
    this.setFormState(prevForm => {
      const index = prevForm.contextAlerts.findIndex(contextAlert => contextAlert.id === contextAlertToCopy.id);
      let newContextAlerts = [...prevForm.contextAlerts];
      newContextAlerts.splice(index + 1, 0, copyContextAlert(contextAlertToCopy));
      return { contextAlerts: newContextAlerts };
    });
  }

  handleNotificationChanged = notification => {
    this.setState(prevState => {
      const notifications = prevState.notifications.map(n => n.id === notification.id ? notification : n);
      
      return {
        changed: true,
        form: mergeFormNotifications(prevState.form, notifications),
        notifications
      };
    });
  }

  handleNotificationRemoved = notification => {
    this.setState(prevState => {
      const notifications = prevState.notifications.filter(n => n.id !== notification.id);

      return {
        changed: true,
        form: mergeFormNotifications(prevState.form, notifications),
        notifications
      };
    });
  }

  handleAddNotification = () => {
    this.setState(prevState => {
      const types = [
        'sheetCompleteContacts',
        'rowCompleteContacts',
        'rowApprovedContacts',
        'rowRejectedContacts'
      ];
      const usedTypes = prevState.notifications.map(notification => notification.type);
      const unusedTypes = types.filter(type => usedTypes.indexOf(type) < 0);
      const notifications = [
        ...prevState.notifications,
        {id: uuid(), type: unusedTypes[0], users: []}
      ];
      return {
        changed: true,
        form: mergeFormNotifications(prevState.form, notifications),
        notifications
      };
    });
  }

  handleToggleUserGroups = e => {
    const enableUserGroups = e.target.checked;

    this.setState(prevState => ({
      changed: true,
      enableUserGroups,
      form: {
        ...prevState.form,
        // When user groups are disabled, clear the groups from the form.
        groups: enableUserGroups ? prevState.form.groups : []
      }
    }));
  }

  handleContextAlertChanged = contextAlert => {
    this.setFormState(prevForm => ({
      contextAlerts: prevForm.contextAlerts.map(w => w.id === contextAlert.id ? contextAlert : w)
    }));
  }

  handleRemoveContextAlert = contextAlert => {
    this.setFormState(prevForm => ({
      contextAlerts: prevForm.contextAlerts.filter(w => w.id !== contextAlert.id)
    }));
  }

  handleToggleFormTesters = e => {
    const enableFormTesters = e.target.checked;

    this.setState(prevState => ({
      changed: true,
      enableFormTesters,
      form: {
        ...prevState.form,
        // When testers are disabled, clear the testers from the form.
        betaTestUsers: enableFormTesters ? prevState.form.betaTestUsers : []
      }
    }));
  }

  handleToggleLibraryTemplate = e => {
    const library = e.target.checked;

    //clearing the toggle clears the code with it. a code only means something on a library
    //template, and the api rejects a code on a non-library template
    this.setFormState(prevForm => ({
      library,
      templateCode: library ? prevForm.templateCode : null,
      description: library ? prevForm.description : null,
      category: library ? prevForm.category : null
    }));
  }

  handleTemplateCodeChanged = e => {
    this.setFormState({templateCode: e.target.value});
  }

  handleTemplateDescriptionChanged = e => {
    this.setFormState({description: e.target.value});
  }

  handleTemplateCategoryChanged = e => {
    this.setFormState({category: e.target.value});
  }

  handleToggleFormReviewers = e => {
    const enableFormReviewers = e.target.checked;
    const reviewNotificationTypes = ['rowApprovedContacts', 'rowRejectedContacts'];

    this.setState(prevState => {
      const notifications = enableFormReviewers
        ? prevState.notifications
        : prevState.notifications.filter(n => reviewNotificationTypes.indexOf(n.type) < 0);

      const form = mergeFormNotifications({
        ...prevState.form,
        reviewers: enableFormReviewers ? prevState.form.reviewers : [],
        secondLevelReviewEnabled: enableFormReviewers ? prevState.form.secondLevelReviewEnabled : false,
        secondLevelReviewers: enableFormReviewers ? prevState.form.secondLevelReviewers : []
      }, notifications);

      return {
        changed: true,
        enableFormReviewers,
        enableSecondLevelReview: enableFormReviewers ? prevState.enableSecondLevelReview : false,
        notifications,
        form
      };
    });
  }

  toggleImportQuestion = () => {
    this.setState(prevState => ({
      importQuestion: !prevState.importQuestion
    }));
  }

  toggleAddQuestionOptions = () => {
    this.setState(prevState => ({
      showAddQuestionOptions: !prevState.showAddQuestionOptions
    }));
  }

  handleImportQuestion = question => {
    const {form} = this.state;
    const order = maxSortOrder(form) + 1;

    this.handleAddQuestion({...question, id: uuid(), order, condition: null});
    this.toggleImportQuestion();
  }

  toggleShowElementList = () => {
    this.setState(prevState => ({
      showFormElementList: !prevState.showFormElementList
    }));
  }

  toggleShowAlertsList = () => {
    this.setState(prevState => ({
      showFormAlertsList: !prevState.showFormAlertsList
    }));
  }

  addFormElement = type => {
    this.setFormState(form => ({
      elements: [
        ...form.elements,
        createElement(type, maxSortOrder(form) + 1)
      ]
    }));
  }

  copyFormElement = element => {
    this.setFormState(form => ({
      elements: [
        ...form.elements,
        copyElement(element, element.order)
      ]
    }), () => {
      this.handleSortElements({oldIndex: -1000, newIndex: 0});
    });
  }

  render() {
    const {user, match: {params: {id}}} = this.props;
    const {changed, form, catalog, catalogTypes, userGroups, users, notifications, loading, error, saving,
      validationEnabled, enableUserGroups, enableFormTesters, enableFormReviewers, enableSecondLevelReview, enableBulkReview,
      importQuestion, showAddQuestionOptions, showFormElementList, showFormAlertsList, forms} = this.state;

    if (loading) {
      return (
        <Screen title={id ? 'Edit Form' : 'New Form'} showTitle={false} activeLink="Forms">
          <Loading fullScreen />
        </Screen>
      );
    }

    if (error) {
      return (
        <Screen title={id ? 'Edit Form' : 'New Form'} showTitle={false} activeLink="Forms">
          <Error fullScreen />
        </Screen>
      );
    }

    const questions = [...form.contextQuestions, ...form.questions].filter(q => !q.deleted);
    const elements = [
      ...questions.map(q => ({ type: 'QUESTION', order: q.order, question: q })),
      ...form.elements.map(e => ({ type: 'ELEMENT', order: e.order, element: e }))
    ].sort((a, b) => a.order - b.order);
    const invalidReviewers = form.reviewers.filter(user => !canReviewForms(user));
    const invalidSecondLevelReviewers = (form.secondLevelReviewers || []).filter(user => !canReviewForms(user));
    const firstLevelReviewerIds = new Set(form.reviewers.map(u => u.id));
    const secondLevelReviewerIds = new Set((form.secondLevelReviewers || []).map(u => u.id));
    const maxNotifications = enableFormReviewers ? 4 : 2;

    // Calculate validations.
    const validations = getValidationErrors(form, {disabled: !validationEnabled});
    const allErrors = flattenValidationErrors(validations);
    const isInvalid = allErrors.length > 0;

    return (
      <Screen title={id ? 'Edit Form' : 'New Form'} showTitle={false} activeLink="Forms">
        <Content>
          <Form onSubmit={this.handleSubmit}>
            <Section id="form-name" title="Form Name">
              <Input
                large
                autoFocus
                type="text"
                defaultValue={form.name}
                onBlur={this.handleFormNameChanged}
                invalid={validations.name}
                validationText={validations.name}
              />
            </Section>
            <Section
              id="questions"
              title="Questions"
              instructions="Questions allow you to collect data from your users. At least one question must be required."
              actions={(
                <>
                  <Button.Group>
                    {<ActionButton primary onClick={() => this.handleAddQuestion()}>Add a Question</ActionButton>}
                    <Button id="add-form-element" onClick={this.toggleShowElementList}>Add an Element</Button>
                    {<Button id="add-question-options" onClick={this.toggleAddQuestionOptions}><Icon name="chevron-down" /></Button>}
                  </Button.Group>
                  <Menu relativeTo="add-question-options" open={showAddQuestionOptions} onClose={this.toggleAddQuestionOptions}>
                    <Menu.Option onClick={this.toggleImportQuestion}>Import from another form...</Menu.Option>
                  </Menu>
                  <Menu relativeTo="add-form-element" open={showFormElementList} onClose={this.toggleShowElementList}>
                    <Menu.Option onClick={() => this.addFormElement('HEADER')}>Header</Menu.Option>
                    <Menu.Option onClick={() => this.addFormElement('DESCRIPTION')}>Description</Menu.Option>
                    <Menu.Option onClick={() => this.addFormElement('CALCULATION')}>Calculation</Menu.Option>
                    <Menu.Option onClick={() => this.addFormElement('AGGREGATION')}>Aggregation</Menu.Option>
                  </Menu>
                </>
              )}>
              {validations.questions && (
                <SectionNotification error>{validations.questions}</SectionNotification>
              )}
              <ElementsList
                form={form}
                catalog={catalog}
                catalogTypes={catalogTypes}
                elements={elements}
                validations={validations.questionsById}
                savedQuestionIds={this.state.savedQuestionIds}
                onChange={this.handleQuestionChanged}
                onSharedAnswerSourceRemoved={this.handleSharedAnswerSourceRemoved}
                getSharedAnswerSourceForms={this.getSharedAnswerSourceForms}
                onChangeElement={this.handleElementChanged}
                onAdd={this.handleAddQuestion}
                onRemove={this.handleRemoveQuestion}
                onDeleteElement={this.handleRemoveElement}
                onSortEnd={this.handleSortElements}
                useWindowAsScrollContainer={true}
                useDragHandle={true}
                lockAxis="y"
                copyFormElement={this.copyFormElement}
              />
            </Section>
            <Section
              id="permissions"
              title="Permissions"
              instructions="Permissions allow you to determine who is allowed to interact with this form in different ways.">
              {this.state.dialog && (
                <AlertPopup>
                  <Card>
                    <Card.Content>
                      <AlertDialog
                        {...this.state.dialog}
                        onOk={this.closeAlertDialog}
                      />
                    </Card.Content>
                  </Card>
                </AlertPopup>
              )}
              <FormSetting
                id="user-groups"
                title="Restrict access to user groups"
                instructions="Only share this form with user groups that you choose."
                value={enableUserGroups}
                onChange={this.handleToggleUserGroups}>
                <ChipInput
                  placeholder="Enter a user group"
                  suggestions={userGroups}
                  value={form.groups}
                  onChange={this.handleUserGroupsChanged}
                />
              </FormSetting>
              {!form.published && (
                <FormSetting
                  id="form-testers"
                  title="Enable form testers"
                  instructions="Test your form before you publish it."
                  value={enableFormTesters}
                  onChange={this.handleToggleFormTesters}>
                  <ChipInput
                    placeholder="Enter a username"
                    helpText={`Choose a maximum of ${this.state.maxTesters} users. All test data will be deleted when you edit or publish the form.`}
                    suggestions={users}
                    value={form.betaTestUsers}
                    onChange={this.handleTestersChanged}
                    disabled={form.betaTestUsers.length >= this.state.maxTesters}
                  />
                </FormSetting>
              )}
              {flags.isOn(user, 'FORM_REVIEWERS_ENABLED') && (
                <FormSetting
                  id="form-reviewers"
                  title="Enable form reviewers"
                  instructions="Select users who will review and either approve or reject form submissions on the dashboard."
                  value={enableFormReviewers}
                  onChange={this.handleToggleFormReviewers}>
                  <ChipInput
                    placeholder="Enter a username"
                    suggestions={users.filter(u => !secondLevelReviewerIds.has(u.id))}
                    value={form.reviewers}
                    onChange={this.handleReviewersChanged}
                  />
                  {invalidReviewers.length > 0 && (
                    <Notification mini warning>
                      When you save this form&nbsp;
                      {friendlyList(invalidReviewers.map(u => <strong key={u.id}>{u.userName}</strong>))}
                      &nbsp;will be promoted to <strong>Reviewer</strong>.
                    </Notification>
                  )}
                </FormSetting>
              )}
              {form.reviewers.length > 0 && (
                <FormSetting
                    id="bulk-review"
                    title="Allow bulk review"
                    instructions="Let reviewers filter this form's data and approve or reject every pending row at once."
                    value={enableBulkReview}
                    onChange={this.handleToggleBulkReview}
                />
              )}
              {flags.isOn(user, 'INVENTORY_ENABLED') && form.reviewers.length > 0 && questions.some(isInventoryQuestion) && (
                <FormSetting
                  id="inventory-post-after-approval"
                  title="Post inventory only after approval"
                  instructions="Inventory answers move stock only once their submission is approved. Rejected submissions never count."
                  value={form.inventoryPostAfterApproval || false}
                  onChange={this.handleToggleInventoryPostAfterApproval}
                />
              )}
              {flags.isOn(user, 'FORM_REVIEWERS_ENABLED') && enableFormReviewers && (
                <FormSetting
                  id="second-level-review"
                  title="Enable second-level review"
                  instructions="Add a second stage of review. First-level reviewers move submissions to In Review; second-level reviewers give the final Approved or Rejected decision."
                  value={enableSecondLevelReview}
                  onChange={this.handleToggleSecondLevelReview}>
                  <ChipInput
                    placeholder="Enter a username"
                    suggestions={users.filter(u => !firstLevelReviewerIds.has(u.id))}
                    value={form.secondLevelReviewers || []}
                    onChange={this.handleSecondLevelReviewersChanged}
                  />
                  {invalidSecondLevelReviewers.length > 0 && (
                    <Notification mini warning>
                      When you save this form&nbsp;
                      {friendlyList(invalidSecondLevelReviewers.map(u => <strong key={u.id}>{u.userName}</strong>))}
                      &nbsp;will be promoted to <strong>Reviewer</strong>.
                    </Notification>
                  )}
                </FormSetting>
              )}
              {flags.isOn(user, 'TEMPLATE_LIBRARY_AUTHORING') && (
                <FormSetting
                  id="library-template"
                  title="Publish as a template"
                  instructions="Offer this form as a prebuilt template that other accounts can deploy. Give it a code to restrict it to the accounts you share the code with, or leave the code empty to make it available to everyone."
                  value={form.library || false}
                  onChange={this.handleToggleLibraryTemplate}>
                  <Input
                    id="template-code"
                    label="Template code"
                    type="text"
                    placeholder="e.g. AAFC2026"
                    helpText="Codes are matched ignoring case. Several templates can share one code so that a single code deploys a whole set of forms. Leave empty to make this template available without a code."
                    value={form.templateCode || ''}
                    onChange={this.handleTemplateCodeChanged}
                  />
                  <Input
                    id="template-category"
                    label="Category"
                    type="text"
                    placeholder="e.g. Operational"
                    helpText="Templates are grouped under this heading in the template picker."
                    value={form.category || ''}
                    onChange={this.handleTemplateCategoryChanged}
                  />
                  <Input
                    id="template-description"
                    label="Description"
                    type="text"
                    placeholder="What this template is for"
                    helpText="Shown on the template card in the picker."
                    value={form.description || ''}
                    onChange={this.handleTemplateDescriptionChanged}
                  />
                </FormSetting>
              )}
            </Section>
            <Section
              id="notifications"
              title="Notifications"
              instructions="Notifications will notify specific people when a sheet is completed, or a set of questions are completed."
              actions={
                <ActionButton
                  disabled={notifications.length === maxNotifications}
                  onClick={this.handleAddNotification}>
                  Add a Notification
                </ActionButton>
              }>
              {notifications.length < 1 ? (
                <Placeholder height="6rem">
                  No notifications yet!
                </Placeholder>
              ) : notifications.map(notification => (
                <CardContainer key={notification.id}>
                  <NotificationBuilder
                    form={form}
                    catalog={catalog}
                    notifications={notifications}
                    notification={notification}
                    users={users}
                    reviewersEnabled={enableFormReviewers}
                    onChange={this.handleNotificationChanged}
                    onRemove={this.handleNotificationRemoved}
                  />
                </CardContainer>
              ))}
            </Section>
            <Section
              id="alerts"
              title="Alerts"
              instructions={
                <>
                  {"Alerts will notify specific people or direct the user to another form when triggered by a configurable condition. Alerts do not require any contacts to be configured."}
                  <br />
                  {"Launch alerts will not email anyone and do not support chaining; navigating to a form via a launch alert will not activate any launch alerts set on the destination form."}
                </>
              }
              actions={
                <Button.Group>
                  <Button id="add-form-alert" onClick={this.toggleShowAlertsList}>Add an alert</Button>
                </Button.Group>              }>
              {form.alerts.length < 1 && form.contextAlerts.length < 1 ? (
                <Placeholder height="6rem">
                  No alerts yet!
                </Placeholder>
              ) : (
                  <>
                    {form.alerts.map(alert => (
                      <CardContainer key={alert.id}>
                        <AlertBuilder
                          form={form}
                          catalog={catalog}
                          users={users}
                          alert={alert}
                          validations={validations.alertsById[alert.id]}
                          onChange={this.handleAlertChanged}
                          onRemove={this.handleRemoveAlert}
                          onCopy={this.handleCopyAlert}
                        />
                      </CardContainer>
                    ))}
                    {form.contextAlerts.map(contextAlert => (
                      <CardContainer key={contextAlert.id}>
                        <ContextAlertBuilder
                          form={form}
                          forms={forms}
                          catalog={catalog}
                          users={users}
                          contextAlert={contextAlert}
                          validations={validations.contextAlertsById[contextAlert.id]}
                          onChange={this.handleContextAlertChanged}
                          onRemove={this.handleRemoveContextAlert}
                          onCopy={this.handleCopyContextAlert}
                        />
                      </CardContainer>
                    ))}
                  </>
                )}
                <Menu relativeTo="add-form-alert" open={showFormAlertsList} onClose={this.toggleShowAlertsList}>
                <Menu.Option onClick={this.handleAddAlert}>Current Sheet</Menu.Option>
                <Menu.Option onClick={this.handleAddContextAlert}>Launch Form</Menu.Option>
              </Menu>
            </Section>
            <Section>
              <MobileSaveButton
                block
                large
                disabled={isInvalid || saving}
                type="submit">
                {saving ? 'Saving...' : (id ? 'Save Form' : 'Create Form')}
              </MobileSaveButton>
            </Section>
            <br />
            <Checklist
              form={form}
              notifications={notifications}
              saving={saving}
              disabled={isInvalid}
              isNewForm={!id}
              validationEnabled={validationEnabled}
            />
          </Form>
        </Content>
        <ImportQuestionModal
          open={importQuestion}
          onClose={this.toggleImportQuestion}
          onImport={this.handleImportQuestion}
        />
        <Prompt when={changed} message="You have unsaved changes. Are you sure you want to leave?" />
      </Screen>
    );
  }
}

const ElementsList = SortableContainer(
  ({form, catalog, catalogTypes, elements, validations, savedQuestionIds, onChange, onSharedAnswerSourceRemoved, getSharedAnswerSourceForms, onAdd, onRemove, onChangeElement, onDeleteElement, copyFormElement}) => (
    <div>
      {elements.map((element, i) => {
        switch (element.type) {
          case 'QUESTION':
            return (
              <Question
                key={element.question.id}
                index={i}
                form={form}
                catalog={catalog}
                catalogTypes={catalogTypes}
                question={element.question}
                validations={validations}
                existsOnServer={savedQuestionIds.has(element.question.id)}
                onChange={onChange}
                onSharedAnswerSourceRemoved={onSharedAnswerSourceRemoved}
                getSharedAnswerSourceForms={getSharedAnswerSourceForms}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            );
          case 'ELEMENT':
            return (
              <Element
                key={element.element.id}
                element={element.element}
                index={i}
                form={form}
                catalog={catalog}
                onChange={onChangeElement}
                onDelete={onDeleteElement}
                copyFormElement={copyFormElement}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  )
);

const Question = SortableElement(({form, catalog, catalogTypes, question, validations, existsOnServer, onChange, onSharedAnswerSourceRemoved, getSharedAnswerSourceForms, onAdd, onRemove}) => (
  <CardContainer id={`question-${question.id}`}>
    <QuestionBuilder
      form={form}
      catalog={catalog}
      catalogTypes={catalogTypes}
      question={question}
      validations={validations[question.id]}
      disableDelete={(form.contextQuestions.filter(q => !q.deleted).length + form.questions.filter(q => !q.deleted).length <= 1)}
      existsOnServer={existsOnServer}
      onChange={onChange}
      onSharedAnswerSourceRemoved={onSharedAnswerSourceRemoved}
      getSharedAnswerSourceForms={getSharedAnswerSourceForms}
      onAdd={onAdd}
      onRemove={onRemove}
      originalQuestionList={form.originalQuestionList}
    />
  </CardContainer>
));

const Element = SortableElement(({form, catalog, element, onChange, onDelete, copyFormElement}) => (
  <CardContainer>
    <ElementBuilder 
      form={form}
      catalog={catalog}
      element={element} 
      onChange={onChange}
      onDelete={onDelete}
      copyFormElement={copyFormElement}
    />
  </CardContainer>
));

function maxSortOrder(form) {
  return Math.max(
    form.questions.length + form.contextQuestions.length + form.elements.length,
    Math.max(...form.questions.map(q => q.order)),
    Math.max(...form.contextQuestions.map(q => q.order)),
    Math.max(...form.elements.map(e => e.order))
  );
}

// Copying and importing a question both come through here. The copy is a new question, so it leaves the shared answer
// settings behind (the api drops them from a new question as well). An inventory copy keeps reading its lot from the
// same question when that question is on this form; an imported one goes back to entering the lot itself.
function copyQuestion(question, order, form) {
  const idsOnForm = [...form.contextQuestions, ...form.questions]
    .filter(q => !q.deleted)
    .reduce((map, q) => {
      map[q.id] = q.id;
      return map;
    }, {});

  return {
    ...withInventoryLotRemapped(withoutSharedAnswerSettings(question), idsOnForm),
    id: uuid(),
    order
  }
}

function createAlert() {
  return {
    id: uuid(),
    name: '',
    alertContacts: [],
    expression: null,
    mobileAlert: true
  };
}

function createContextAlert() {
  return {
    id: uuid(),
    name: '',
    description: '',
    formId: '',
    expression: null,
    required: false
  };
}

function copyAlert(alert) {
  return {
    id: uuid(),
    name: alert.name,
    alertContacts: alert.alertContacts,
    expression: alert.expression,
    mobileAlert: alert.mobileAlert ? alert.mobileAlert : true
  };
}

function copyContextAlert(contextAlert) {
  return {
    id: uuid(),
    name: contextAlert.name,
    description: contextAlert.description,
    formId: contextAlert.formId,
    expression: contextAlert.expression,
    required: contextAlert.required
  };
}

function createElement(type, order) {
  let elementDefaults = {};

  switch (type) {
    case 'HEADER':
      elementDefaults = {
        label: ''
      };
      break;
    case 'DESCRIPTION':
      elementDefaults = {
        label: ''
      };
      break;
    case 'CALCULATION':
      elementDefaults = {
        label: '',
        defaultValue: 0,
        formula: null
      };
      break;
    case 'AGGREGATION': 
      elementDefaults = {
        label: '',
        defaultValue: 0,
        aggregation: 'SUM',
        formula: null
      };
      break;
    default:
      throw new Error('Unknown element type: ' + type);
  }

  return {
    id: uuid(),
    type,
    order,
    data: elementDefaults,
    condition: null,
    formElementMeta: []
  }
}

function copyElement(element, order) {
  return {
    id: uuid(),
    type: element.type,
    context: element.context,
    order,
    data: element.data,
    condition: element.condition,
    formElementMeta: element.formElementMeta,
    preview: element.preview,
    previewOrder: element.previewOrder
  }
}

function mergeFormNotifications(form, notifications) { 
  const notificationsByType = notifications.reduce((map, notification) => {
    map[notification.type] = notification.users;
    return map;
  }, {});

  return {
    ...form,
    sheetCompleteContacts: [],
    rowCompleteContacts: [],
    rowApprovedContacts: [],
    rowRejectedContacts: [],
    ...notificationsByType
  };
}

function canReviewForms(user) {
  return !(user.role === 'USER' || user.role === 'ANALYST');
}

/**
 * Converts and array such as:
 *  ['one', 'two', 'three']
 * into:
 *  ['one', ', ', 'two', ' and ', 'three']
 */
function friendlyList(items) {
  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return items[0];
  }

  if (items.length === 2) {
    return [items[0], ' and ', items[1]];
  }

  return [
    ...items.slice(0, -1).reduce((acc, next) => acc === null ? [next] : [...acc, ', ', next], null),
    ' and ',
    items[items.length - 1]
  ];
}

const Content = styled(UnstyledContent)`
  padding-top: 1.875rem;
  margin-right: 25rem;

  @media (max-width: 75rem) {
    margin-right: 0;
    padding-top: 1rem;
  }
`;

const Section = styled(({title, instructions, actions, children, ...props}) => (
  <div {...props}>
    {title && <SectionHeader title={title} instructions={instructions} />}
    {children}
    {actions && <SectionActions>{actions}</SectionActions>}
  </div>
))`
  margin-bottom: 3rem;
`;

const SectionHeader = styled(({title, instructions, ...props}) => (
  <div {...props}>
    <h1>{title}</h1>
    {instructions && (<p>{instructions}</p>)}
  </div>
))`
  margin-bottom: 1.5rem;

  h1 {
    margin-bottom: 0.5rem;
    white-space: nowrap; 
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 800px) {
      font-size: 2.25rem;
    }
  }

  p {
    margin: 0;
    color: #747474;
    line-height: 1.5;
  }
`;

const SectionNotification = styled(Notification)`
  margin-bottom: 1rem;
`;

const SectionActions = styled.div`
  margin-top: 1rem;
`;

const ActionButton = styled(Button)`
  width: 14rem;
`;

const MobileSaveButton = styled(ActionButton)`
  display: none;

  @media (max-width: 75rem) {
    display: block;
    width: 100%;
  }
`;

const CardContainer = styled.div`
  margin-bottom: 1.5rem;

  :last-child {
    margin-bottom: none;
  }
`;

const FormSetting = styled(({id, title, instructions, value, onChange, children, ...props}) => (
  <div {...props}>
    <input id={id} type="checkbox" checked={value} onChange={onChange} />
    <FormSettingContent>
      <label htmlFor={id}>{title}</label>
      <small>{instructions}</small>
      {value && children}
    </FormSettingContent>
  </div>
))`
  display: flex;
  margin-bottom: 1.5rem;

  input[type=checkbox] {
    margin: 0 0.5rem 0 0;
  }
`;

const FormSettingContent = styled.div`
  flex: 1;

  small {
    display: block;
    font-size: 0.875rem;
    color: #747474;
    margin: 0.25rem 0 0.5rem 0;
  }

  ${Notification} {
    margin-top: 0.5rem;
  }
`;

const AlertPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4); 
  z-index: 9999;
`;

export default withUser(FormBuilderScreen);
